import { Component, ViewChild, TemplateRef, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { MessageService } from 'primeng/api';
import { interval, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { TBTabName } from 'src/app/shared/entity/enums';
import { ErrorMessage, ErrorType } from 'src/app/shared/entity/error.const';
import { ApproachType, TransactionType, OptimizationType } from 'src/app/shared/entity/optimise';
import { Routes } from 'src/app/shared/models/kpi-details';
import { DateFormatPipe, DateFormatType } from 'src/app/shared/pipe/date-format-pipe/date-format.pipe';
import { TaskMovementService } from 'src/app/shared/services/operations/task-movement.service';
import { OptimizationService } from 'src/app/shared/services/optimization/optimization.service';
import { TaskInfoService } from 'src/app/shared/services/task-info/task-info.service';

@Component({
    selector: 'app-optimised-sequence',
    templateUrl: './optimised-sequence.component.html',
    styleUrls: [
        './optimised-sequence.component.scss',
        "../../../shared/css/primeng-dropdown.component.scss",
        "../../../shared/css/primeng-table.component.scss"
    ],
    standalone: false
})
export class OptimisedSequenceComponent implements OnInit, OnDestroy {

  constructor(private readonly dateFormatPipe: DateFormatPipe,
              private readonly optimizationService: OptimizationService,
              private readonly messageService: MessageService,
              private readonly taskMovementService: TaskMovementService,
              private readonly taskInfoService: TaskInfoService
    ) { }

  /** ViewChild to access the footer template of the optimised sequence component, 
   * allowing dynamic content insertion in the parent component's dialog footer. */
  @ViewChild('optimisedSequenceFooter', { static: true }) optimisedSequenceFooter!: TemplateRef<any>;

  /** Inputs to receive route details and selected optimization approach from parent component */
  @Input() routesDetails: Routes | null = null;

  /** Input to receive the selected optimization approach from the parent component.*/
  @Input() selectedOptimizationApproach: ApproachType = ApproachType.UNRESTRICTED;

  /** Output event emitter to notify the parent component to close the RPS Optimization Dialog when necessary. */
  @Output() closeRPSOptimizationDialog = new EventEmitter<void>();

  /** Output event emitter to notify the parent component that optimization was accepted successfully. */
  @Output() optimizationAccepted = new EventEmitter<void>();

  /** Timer properties to manage the countdown for user actions after optimization */
  timerSeconds = 15;

  /** Display string for the timer, formatted as MM:SS. */
  timerDisplay = '00:00';

  /** Flag to indicate whether the timer has expired, used to disable buttons after countdown. */
  timerExpired = false;

  /** Subscription object to manage the timer observable, allowing for cleanup on component destruction. */
  private timerSubscription: Subscription | null = null;

  /** data representing the optimised sequence of stops and tasks, used for display in the component. */
  optimisedSequence: any[] = [];

  /** Column definitions for the optimised sequence table, specifying field mappings, headers, and widths. */
  optimisedSequenceColumns = [
    { field: 'old_stop_sequence', header: 'Stop', width: '80px' },
    { field: 'old_task_sequence', header: 'Task', width: '80px' },
    { field: 'new_stop_sequence', header: 'New Stop', width: '120px' },
    { field: 'new_task_sequence', header: 'New Task', width: '120px' },
    { field: 'task_point_name', header: 'Task Point Name', width: '140px' },
    { field: 'address', header: 'Address', width: '90px' },
    { field: 'postal_code', header: 'Postal Code', width: '150px' },
    { field: 'eta', header: 'ETA', width: '90px' },
    { field: 'time_window', header: 'Time Window', width: '150px' },
  ];

  // Flag to indicate whether the optimization was successful, used to display success/error messages and styling in the component.
  isSuccessOptimisation: boolean = false;

  // Flag to indicate whether the optimization process is currently loading, used to display loading indicators and manage UI state.
  isLoading: boolean = false;

  // Before/After KPI metrics for drive time (in hours)
  driveTimeBefore: number = 0;
  driveTimeAfter: number = 0;

  // Before/After KPI metrics for driver distance (in km)
  driverDistanceBefore: number = 0;
  driverDistanceAfter: number = 0;

  // Before/After KPI metrics for total time (in hours)
  totalTimeBefore: number = 0;
  totalTimeAfter: number = 0;

  // Variable to hold error message details for display in the UI when optimization fails
  errorMessage: string = ''; 

  /** Returns 'pn-danger' if after > before, 'pn-success' if after < before, empty string if equal */
  get driveTimeStatus(): string {
    if (this.driveTimeAfter > this.driveTimeBefore) return 'pn-danger';
    if (this.driveTimeAfter < this.driveTimeBefore) return 'pn-success';
    return '';
  }

  get driverDistanceStatus(): string {
    if (this.driverDistanceAfter > this.driverDistanceBefore) return 'pn-danger';
    if (this.driverDistanceAfter < this.driverDistanceBefore) return 'pn-success';
    return '';
  }

  get totalTimeStatus(): string {
    if (this.totalTimeAfter > this.totalTimeBefore) return 'pn-danger';
    if (this.totalTimeAfter < this.totalTimeBefore) return 'pn-success';
    return '';
  }

  // Flag to indicate if there are any errors in the optimized sequence, used to conditionally style the UI and display error indicators.
  hasSomeError = false;

  ngOnInit(): void {
    if(this.routesDetails) {
      this.getOptimisedSequence();
    }
  }

  /**
   * Fetches the optimized sequence of stops and tasks for the current route based on the selected optimization approach.
   * It calls the OptimizationService to get the optimized sequence and updates the component state with the results.
   * The method also handles loading state, success/failure flags, and starts a timer for user actions after optimization.
   */
  getOptimisedSequence(): void {
    this.isLoading = true;
    this.optimizationService.getOptimizedSequence({
      routeId: this.routesDetails?.route_id || '',
      optimizationType: OptimizationType.OPTIMISE,
      approachType: this.selectedOptimizationApproach,
      transactionType: TransactionType.PREVIEW
    }).subscribe({
      next: (response: any) => {
        this.isSuccessOptimisation = true;
        this.mapKpiMetrics(response?.kpi);
        this.optimisedSequence = this.mapOptimisedSequence(response?.tasks || []);
        this.hasSomeError = this.optimisedSequence.some((item: any) => item.error); // check if any item has error property set to true
        this.isLoading = false;
      },
      error: (error: any) => {  
        console.error('Error fetching optimised sequence data:', error);
        if (error?.detail) {
          this.errorMessage = error.detail??ErrorMessage.COMMITOPTIMISATIONERROR; // Set the error message to display in the UI
        }
        this.isLoading = false;
        this.isSuccessOptimisation = false;
        this.timerExpired = true; // Expire the timer immediately on error to disable action buttons
        this.hasSomeError = true; // Set error flag to true to indicate there was an error in fetching the optimized sequence
      },
      complete: () => { 
        this.startTimer(); // Start the timer once the data is fetched and processed
      }
    });
  }

  /**
   * Maps the KPI metrics from the API response, extracting before and after values
   * for drive time, driver distance, and total time.
   */
  private mapKpiMetrics(kpi: any): void {
    const kpiBefore = kpi?.kpi_before;
    const kpiAfter = kpi?.kpi_after;
    this.driveTimeBefore = parseFloat((kpiBefore?.service_time / 3600).toFixed(2)) || this.driveTimeBefore;
    this.driveTimeAfter = parseFloat((kpiAfter?.service_time / 3600).toFixed(2)) || this.driveTimeAfter;
    this.driverDistanceBefore = parseFloat((kpiBefore?.total_drive_distance / 1000).toFixed(2)) || this.driverDistanceBefore;
    this.driverDistanceAfter = parseFloat((kpiAfter?.total_drive_distance / 1000).toFixed(2)) || this.driverDistanceAfter;
    this.totalTimeBefore = parseFloat((kpiBefore?.total_drive_time / 3600).toFixed(2)) || this.totalTimeBefore;
    this.totalTimeAfter = parseFloat((kpiAfter?.total_drive_time / 3600).toFixed(2)) || this.totalTimeAfter;
  }

  /**
   * Maps raw task data into display-ready format by converting 0-based indices to 1-based sequences,
   * formatting ETA and time window values, and flagging changed sequences.
   */
  mapOptimisedSequence(tasks: any[]): any[] {
    return tasks.map((item: any) => {
      const stopChanged = item?.old_stop_sequence !== item?.new_stop_sequence;
      const taskChanged = item?.old_task_sequence !== item?.new_task_sequence;
      return {
        ...item,
        // Convert to 1-based index for display purposes
        old_stop_sequence: item?.old_stop_sequence === -1 ? '-' : item?.old_stop_sequence + 1,
        old_task_sequence: item?.old_task_sequence === -1 ? '-' : item?.old_task_sequence + 1,
        new_stop_sequence: item?.new_stop_sequence === -1 ? '-' : item?.new_stop_sequence + 1,
        new_task_sequence: item?.new_task_sequence === -1 ? '-' : item?.new_task_sequence + 1,

        eta: this.dateFormatPipe.transform(item?.eta??'', DateFormatType.TIME) || '',
        time_window: `${this.dateFormatPipe.transform(item?.time_window?.begin, DateFormatType.TIME) || ''} - ${this.dateFormatPipe.transform(item?.time_window?.end, DateFormatType.TIME) || ''}`,
        stopSequenceChanged: stopChanged,
        taskSequenceChanged: taskChanged
      };
    });
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  /**
   * Starts a countdown timer for 15 seconds, updating the timer display every second.
   * When the timer expires, it sets the timerExpired flag to true, which can be used to disable action buttons in the UI.
   * The method also ensures that any existing timer subscription is cleaned up before starting a new timer.
   */
  startTimer(): void {
    this.timerSeconds = 15;
    this.timerExpired = false;
    this.updateTimerDisplay();
    this.stopTimer();
    
    this.timerSubscription = interval(1000)
      .pipe(take(15))
      .subscribe(() => {
        this.timerSeconds--;
        this.updateTimerDisplay();
        if (this.timerSeconds <= 0) {
          this.onTimerComplete();
        }
      });
  }

  /**
   * Stops the timer by unsubscribing from the timer observable,
   * preventing any further emissions and ensuring proper cleanup of resources.
   */
  stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  /**
   * Updates the timer display string based on the current timer seconds, formatting it as MM:SS.
   */
  private updateTimerDisplay(): void {
    const seconds = this.timerSeconds;
    this.timerDisplay = `00:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Method called when the timer completes, 
   * setting the timerExpired flag to true to disable action buttons and update the UI accordingly.
   */
  private onTimerComplete(): void {
    this.timerExpired = true;
  }

  /**
   * Method to handle the acceptance of the optimization. 
   * It calls the OptimizationService to commit the optimized sequence for the current route.
   * Upon successful application of the optimization, it logs the response. 
   * If there's an error during the process, it logs the error details.
   */
  acceptOptimisation(): void {
    this.stopTimer(); // Stop the timer when user accepts the optimization to prevent any further countdown actions
    // Logic to accept the optimization and apply the changes
    this.optimizationService.getOptimizedSequence({
      routeId: this.routesDetails?.route_id || '',
      optimizationType: OptimizationType.OPTIMISE,
      approachType: this.selectedOptimizationApproach,
      transactionType: TransactionType.COMMIT
    }, true).subscribe({
      next: (response: any) => {
        /**
         * To update the data set
         */
        if(this.taskInfoService.selectedTabName.toLowerCase() === TBTabName.Planned.toLowerCase()) {
          this.taskMovementService.refereshTabsData();
        }
        this.closeRPSOptimizationDialog.emit(); // Notify parent component to close the dialog after applying optimization
        this.optimizationAccepted.emit(); // Notify parent to refresh route optimization details
        this.messageService.add({
          severity: ErrorType.SUCCESS,
          closable: false,
          summary: "",
          detail: response?.message ?? ErrorMessage.COMMITOPTIMISATIONSUCCESS,
        });
      },
      error: (error: any) => {
        this.closeRPSOptimizationDialog.emit(); // Notify parent component to close the dialog after applying optimization
        this.messageService.add({
          severity: ErrorType.ERROR,
          closable: false,
          summary: "",
          detail: error?.message ?? ErrorMessage.COMMITOPTIMISATIONERROR,
        });
      }
    });
  }
  
  // Logic to reject the optimization and discard the changes
  rejectOptimisation(): void {
    this.closeRPSOptimizationDialog.emit(); // Notify parent component to close the dialog after rejecting optimization
  }

}
